
import { Button, Modal, Space } from 'antd';
import { useEffect, useState } from 'react';

function detectBrowser(): string {
    const ua = navigator.userAgent;

    if (/Chrome/.test(ua) && !/Edg/.test(ua) && !/OPR/.test(ua)) return 'Chrome';
    if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari';
    if (/Firefox/.test(ua)) return 'Firefox';
    if (/Edg/.test(ua)) return 'Edge';
    if (/OPR/.test(ua)) return 'Opera';

    return 'Other';
}

const browserHelpLinks: Record<string, { name: string; url: string }> = {
    Chrome: {
        name: 'Google Chrome',
        url: 'https://support.google.com/accounts/answer/61416',
    },
    Firefox: {
        name: 'Mozilla Firefox',
        url: 'https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences',
    },
    Safari: {
        name: 'Safari',
        url: 'https://support.apple.com/en-us/HT201265',
    },
    Edge: {
        name: 'Microsoft Edge',
        url: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09',
    },
    Opera: {
        name: 'Opera',
        url: 'https://help.opera.com/en/latest/web-preferences/#cookies',
    },
    Other: {
        name: 'Browser settings',
        url: 'https://www.whatismybrowser.com/guides/how-to-enable-cookies/auto',
    }
};

export default function CookiesWarning() {
    const [storageBlocked, setStorageBlocked] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [browser, setBrowser] = useState<string>('Other');

    useEffect(() => {
        setBrowser(detectBrowser());

        let cookieBlocked = false;
        let localStorageBlocked = false;

        try {
            document.cookie = '_cc=1; SameSite=Lax';
            cookieBlocked = document.cookie.indexOf('_cc=') === -1;
            document.cookie = '_cc=; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
        } catch (e) {
            console.warn('Cookie check failed:', e);
            cookieBlocked = true;
        }

        try {
            localStorage.setItem('_ls_test', '1');
            const test = localStorage.getItem('_ls_test');
            if (test !== '1') {
                localStorageBlocked = true;
            }
            localStorage.removeItem('_ls_test');
        } catch (e) {
            console.warn('LocalStorage check failed:', e);
            localStorageBlocked = true;
        }

        if (cookieBlocked || localStorageBlocked) {
            setStorageBlocked(true);
        }

    }, [showModal]);

    if (!storageBlocked) return null;

    const help = browserHelpLinks[browser];

    return (
        <div className='mt-[80px]'>
            <Modal
                open={storageBlocked}
                classNames={{
                    body: "!p-2"
                }}
                closable={false}
                footer={false}
                width={400}
                centered
            >

                {showModal ? (
                    <div className='flex flex-col items-center gap-3'>
                        <h2 className='font-medium mb-2'>How to Enable Cookies</h2>
                        <p className='text-center text-gray-600'>
                            To keep your account secure and the app working properly, please enable cookies and local storage in your browser:
                        </p>
                        <ul className='mb-4 text-gray-600 space-y-2 text-sm'>
                            <li>⚙️ Go to your browser settings</li>
                            <li>🔒 Allow cookies and local storage</li>
                            <li>🔄 Reload this page</li>
                        </ul>

                        <div className='w-full text-left text-sm text-gray-500 space-y-2 text-center'>
                            <p className='font-medium'>You're using: <span className='text-gray-700'>{help.name}</span></p>
                            <a
                                href={help.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-blue-600 hover:underline'
                            >
                                Learn how to enable cookies in {help.name}
                            </a>
                        </div>

                        <Button
                            type='text'
                            className='!bg-gray-100 hover:!bg-gray-200 mt-4'
                            onClick={() => {
                                setShowModal(false);
                                window.location.reload();
                            }}
                        >
                            Close & Reload
                        </Button>
                    </div>
                ) : (
                    <div className='flex flex-col items-center gap-4'>
                        <svg width="37" height="36" viewBox="0 0 37 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.25" width="36" height="36" rx="18" fill="#FEF0C7" />
                            <g clipPath="url(#clip0_998_7145)">
                                <path d="M18.25 15.3334V18M18.25 20.6667H18.2567M24.9167 18C24.9167 21.6819 21.9319 24.6667 18.25 24.6667C14.5681 24.6667 11.5833 21.6819 11.5833 18C11.5833 14.3181 14.5681 11.3334 18.25 11.3334C21.9319 11.3334 24.9167 14.3181 24.9167 18Z" stroke="#DC6803" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                                <clipPath id="clip0_998_7145">
                                    <rect width="16" height="16" fill="white" transform="translate(10.25 10)" />
                                </clipPath>
                            </defs>
                        </svg>
                        <p className='text-center text-sm text-gray-700'>
                            Looks like cookies are turned off. They're required for login and a smooth experience. We'll walk you through how to enable them in your browser.
                        </p>
                        <Space>
                            <Button type='text' className='!bg-gray-100 hover:!bg-gray-200' onClick={() => setShowModal(true)}>See how to enable Cookies</Button>
                        </Space>
                    </div>
                )}
            </Modal>
        </div>
    );
}
